import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollFadeIn } from '../hooks/useScrollFadeIn';
const IMAGES = ['https://sprinklenet.com/wp-content/uploads/2026/03/01-connection.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/02-purpose.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/03-freedom.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/04-growth.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/05-expression.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/06-belonging.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/07-achievement.png', 'https://sprinklenet.com/wp-content/uploads/2026/03/08-peace.png'];
interface ImageExerciseProps {
  onComplete: (selectedImages: number[]) => void;
  isComplete?: boolean;
}
export function ImageExercise({
  onComplete,
  isComplete = false
}: ImageExerciseProps) {
  const {
    ref,
    isVisible
  } = useScrollFadeIn();
  const [currentRound, setCurrentRound] = useState(0);
  const [selectedImages, setSelectedImages] = useState<number[]>([]);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const totalRounds = 8;
  const [transitionSpeed, setTransitionSpeed] = useState(0.35);
  const advance = (select: boolean, fast = false) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setTransitionSpeed(fast ? 0.2 : 0.35);
    const newSelected = select ? [...selectedImages, currentRound] : [...selectedImages];
    if (select) setSelectedImages(newSelected);
    const delay = fast ? 200 : 400;
    setTimeout(() => {
      if (currentRound < totalRounds - 1) {
        setCurrentRound(prev => prev + 1);
        setIsTransitioning(false);
      } else {
        onComplete(newSelected);
        setTimeout(() => {
          document.getElementById('results')?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 600);
      }
    }, delay);
  };
  return <motion.section id="image-exercise" className="bg-white px-4 md:px-8 flex flex-col justify-center overflow-hidden" animate={isComplete ? {
    height: 0,
    opacity: 0,
    paddingTop: 0,
    paddingBottom: 0
  } : {
    height: 'auto',
    opacity: 1,
    paddingTop: '3rem',
    paddingBottom: '0.5rem'
  }} transition={{
    duration: 0.5,
    ease: 'easeInOut'
  }} style={{
    paddingTop: '3rem',
    paddingBottom: '0.5rem'
  }} data-id="element-126">
      <motion.div ref={ref} initial={{
      opacity: 0,
      y: 20
    }} animate={isVisible ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 20
    }} transition={{
      duration: 0.6
    }} className="max-w-xl mx-auto w-full" data-id="element-127">
        <h2 className="text-navy text-2xl md:text-3xl text-center font-semibold mb-2" data-id="element-128">
          Does this image speak to you?
        </h2>
        <p className="text-gray-500 text-center text-sm mb-8" data-id="element-129">
          Go with your gut — tap the image or hit "This Resonates" to select it.
        </p>

        {/* Progress bar */}
        <div className="mb-8" data-id="element-130">
          <div className="flex justify-between text-sm text-gray-500 mb-2 font-medium" data-id="element-131">
            <span data-id="element-132">Progress</span>
            <span data-id="element-133">
              Image {currentRound + 1} of {totalRounds}
            </span>
          </div>
          <div className="w-full h-2 rounded-full bg-gray-200 overflow-hidden" data-id="element-134">
            <motion.div className="h-full bg-teal rounded-full" initial={{
            width: `${currentRound / totalRounds * 100}%`
          }} animate={{
            width: `${(currentRound + 1) / totalRounds * 100}%`
          }} transition={{
            duration: 0.3
          }} data-id="element-135" />
          </div>
        </div>

        {/* Single image display */}
        <div className="flex flex-col items-center" data-id="element-136">
          <AnimatePresence mode="wait" data-id="element-137">
            <motion.div key={currentRound} initial={{
            opacity: 0,
            x: 40
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -40
          }} transition={{
            duration: transitionSpeed,
            ease: 'easeInOut'
          }} className="w-full max-w-[500px]" data-id="element-138">
              <div className="relative rounded-xl overflow-hidden shadow-lg aspect-square max-h-[60vh] mx-auto cursor-pointer active:scale-[0.98] transition-transform" onClick={() => advance(true, true)} role="button" aria-label="Select this image" data-id="element-139">
                <img src={IMAGES[currentRound]} alt={`Image ${currentRound + 1}`} className="w-full h-full object-cover" draggable={false} data-id="element-140" />
                {/* DEMO IMAGE diagonal banner */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden" data-id="element-141">
                  <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-black/30 py-1 text-center" style={{
                  width: '150%'
                }} data-id="element-142">
                    <span className="text-white/50 text-xs md:text-sm font-semibold tracking-widest uppercase" data-id="element-143">
                      Demo Image
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Action buttons */}
          <div className="flex gap-3 md:gap-4 mt-6 md:mt-8 w-full max-w-[500px]" data-id="element-144">
            <button onClick={() => advance(false)} disabled={isTransitioning} className="flex-1 py-3 px-4 md:px-6 rounded-xl border-2 border-gray-300 text-gray-500 font-semibold text-sm md:text-base hover:border-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50" data-id="element-145">
              Skip
            </button>
            <button onClick={() => advance(true)} disabled={isTransitioning} className="flex-1 py-3 px-4 md:px-6 rounded-xl bg-teal text-white font-semibold text-sm md:text-base shadow-md hover:bg-teal/90 transition-colors disabled:opacity-50" data-id="element-146">
              This Resonates
            </button>
          </div>
        </div>
      </motion.div>
    </motion.section>;
}